import * as React from "react";

import "./category-list-route.scss";

import {Helmet}             from "@Common/libs/helmet/helmet";
import {cs}                 from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {UseState}           from "@Common/react/use-state";
import {chain}              from "@Common/utils/fs";
import {isEmpty, oMapToArr} from "@Common/utils/objects";
import {isBlankStr}         from "@Common/utils/strings";

import {Breadcrumbs}   from "../common/breadcrumbs/breadcrumbs";
import {AGENT_LEVEL}   from "../common/logic/user-level";
import {rProductTiles} from "../common/tiles/product-tiles";
import {Layout}        from "../layout/layout";

import {rSubViewMore} from "./sub-view-more";

export const CategoryListRoute = ({disableSuggestionsDialog=false}:{disableSuggestionsDialog?: boolean}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("catSubStyle"),
    consumeContext("userLevels"),
    ["exclusiveContent", ({}, next) => next(sessionStorage.getItem("exclusiveContent"))],
    ["listSelected", ({}, next) => UseState({
        initValue: !!sessionStorage.getItem("listingId"),
        next
    })],
    ["mailingOnly", ({}, next) => UseState({
        initValue: !!sessionStorage.getItem("mailingOnly"),
        next
    })],
    ["catSubStyle", ({catSubStyle, userLevels, exclusiveContent, mailingOnly}, next) => next(!catSubStyle ? null : chain(
        catSubStyle?.value,
        (_) => oMapToArr(_, (v, k) => ({...v, id: k})),
        (_) => userLevels?.userLevel !== AGENT_LEVEL ? _ : _.filter((cat) => (
            !cat.exclusiveContentJson ||
            JSON.parse(cat.exclusiveContentJson).filter(
                value => exclusiveContent && isValidJsonString(exclusiveContent) && typeof JSON.parse(exclusiveContent) === 'object' ? (
                    Object.keys(JSON.parse(exclusiveContent))?.includes(value)
                ) : (
                    exclusiveContent?.split(',').includes(value)
                )
            ).length > 0
        )),
        (_) => userLevels?.listType && userLevels?.listType != 6 ? _.filter((cat) => cat.hasNonNdmTemplate) : _,
        (_) => userLevels?.listType && userLevels?.listType != 6
            ? _.map(cat => ({
                ...cat,
                child: Array.isArray(cat.child)
                    ? cat.child.filter(sub => sub.hasNonNdmTemplate)
                    : []
            }))
            : _,
        (_) => sessionStorage.getItem("listingId") ? _.filter((cat) => cat.listingsEligible) : _,
        (_) => mailingOnly?.value ? _.filter((cat) => cat.addressable && cat.mailable).map(cat => ({
            ...cat,
            child: Array.isArray(cat.child)
                ? cat.child.filter(sub => sub.addressable && cat.mailable)
                : []
        })) : _,
        (_) => userLevels?.listSelected ? _.filter((cat) => cat.addressable) : _,
        (_) => !sessionStorage.getItem("listingId") && !sessionStorage.getItem("mailingOnly") && !userLevels?.listSelected ? _.filter((cat) => !cat.program) : _,
        (_) => _.sort((a, b) => {
                return a.seq - b.seq;
        }),
    ))],
    consumeContext("themeScheme"),
    ({themeScheme}, next) => (
        <>
            <Helmet title={
                themeScheme.companyName === 'Xpressdocs' ? (
                    "Xpressdocs"
                ) : (
                    themeScheme.companyName + " Print Provider"
                )
            }>
                <meta name="keywords" content={`${
                    themeScheme.companyName.split(' ').join(',')
                },Xpressdocs,Print,Provider`.replace(/[^a-zA-Z0-9]+/g, ',')}/>
            </Helmet>
            {next()}
        </>
    ),
    ({company, catSubStyle, listSelected, mailingOnly}) => (
        <Layout
            className="category-list-route-3ejc"
            active="catSubcatStyle"
            disableSuggestionsDialog={disableSuggestionsDialog}
        >

            <div className="restrict-width-1">

                <Breadcrumbs {...{listSelected, mailingOnly}}/>

                {catSubStyle == null ? (
                    <div className="" xd-qa-type="label" xd-qa-name="Loading">
                        Loading...
                    </div>
                ) : isEmpty(catSubStyle) ? (
                    <div className="" xd-qa-type="label" xd-qa-name="Empty">
                        No templates are available in this category!
                    </div>
                ) : (
                    rProductTiles({
                        products: catSubStyle,
                        getProductViewData: (cat) => ({
                            img_url: cat.image,
                            name: cat.name,
                            type: cat.categoryType,
                            ...( (!isBlankStr(cat.link) && cat.link.startsWith("/app/")) ? {
                                href: null,
                                to: cat.link.substring(cat.link.indexOf("/app") + ("/app").length ),
                            } : {
                                href: isBlankStr(cat.link) || cat.program ? null : cat.link,
                                to: (cat.child.length === 1 && !cat.child[0].link) ? `/cat/${cat.id}/sub/${cat.child[0].id}` : `/cat/${cat.id}`,
                            }),
                            new_tab:cat.newTab,
                            getIcon: () => company?.prefs.smartTemplateIcon && cat.hasCCTemplate && (
                                <div className="icon" xd-qa-type="icon" xd-qa-name="Smart Template Icon">
                                    <img alt="Smart Template" src="/next_images/icons/SmartTemplateIcon.svg"/>
                                </div>
                            ),
                            children: cat.child.sort((sub1, sub2)=> (sub1.seq - sub2.seq))
                                .map((sub) => ({
                                    name: sub.name,
                                    new_tab:sub.newTab,
                                    ...( (!isBlankStr(sub.link) && sub.link.startsWith("/app/")) ? {
                                        href: null,
                                        to: sub.link.substring(sub.link.indexOf("/app/") + ("/app/").length ),
                                    } : {
                                        href: isBlankStr(sub.link) ? null : sub.link,
                                        to: `/cat/${cat.id}/sub/${sub.id}`,
                                    }),
                                })),
                        }),
                        view_more: {
                            getItems: (cat) => cat.child,
                            renderItem: rSubViewMore,
                        },
                    })
                )}
            </div>
        </Layout>
    ),
);

const isValidJsonString = (str) => {
    try {
        JSON.parse(str);
        return true;
    } catch (error) {
        return false;
    }
}
